import type { PresentationSlide, PresentationSourceSnapshot, ProjectRow } from '../types'
import { buildCanvasProjection } from '../lib/canvasProjection'
import { CANVAS_RENDERER_VERSION, canvasSceneProjectionSignature } from '../modules/canvas3d/captureBinding'

const TEMPLATE_SLIDE: Record<PresentationSlide['key'], number> = {
  cover: 1,
  process: 2,
  process_detail: 8,
  current: 3,
  progress: 4,
  dates: 5,
  derogation: 6,
  differences: 7,
  attachments: 8,
  next_actions: 9,
  edition: 10,
}

const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'

function bullets(values: string[]) {
  return values.filter(Boolean).map((value) => `• ${value}`).join('\n\n')
}

function xmlEscape(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;')
}

function replaceTokens(xml: string, tokens: Record<string, string>) {
  let result = xml.replaceAll('[[MODULES_SUMMARY]]', xmlEscape(tokens.CONTENT_SUMMARY ?? ''))
  for (const [key, value] of Object.entries(tokens)) {
    result = result.replaceAll(`[[${key}]]`, xmlEscape(value))
  }
  return result.replaceAll('MODULES PRÉSENTÉS', 'CONTENU').replace(/\[\[[A-Z0-9_]+\]\]/g, '')
}

function replaceSequenceLabels(xml: string, slide: PresentationSlide) {
  const start = slide.sequenceStart ?? 0
  const itemCount = slide.items?.length ?? 0
  const labels = slide.key === 'derogation'
    ? Array.from({ length: 3 }, (_item, index) => [`D${index + 1}`, index < itemCount ? `D${start + index + 1}` : ''] as const)
    : slide.key === 'process_detail'
      ? Array.from({ length: 5 }, (_item, index) => [String(index + 1).padStart(2, '0'), index < itemCount ? String(start + index + 1).padStart(2, '0') : ''] as const)
      : []
  return labels.reduce((result, [before, after]) => result.replace(`<a:t>${before}</a:t>`, `<a:t>${after}</a:t>`), xml)
}

function withSlideEntries(xml: string, count: number) {
  const entries = Array.from({ length: count }, (_item, index) => `<p:sldId id="${256 + index}" r:id="rIdSuiviAziz${index + 1}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"/>`)
  return xml.replace(/<p:sldIdLst>[\s\S]*?<\/p:sldIdLst>/, `<p:sldIdLst>${entries.join('')}</p:sldIdLst>`)
}

function withSlideRelationships(xml: string, count: number) {
  const withoutSlides = xml.replace(/<Relationship\b(?=[^>]*\/relationships\/slide")[^>]*\/>/g, '')
  const relationships = Array.from({ length: count }, (_item, index) => `<Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/slide" Target="/ppt/slides/slide${index + 1}.xml" Id="rIdSuiviAziz${index + 1}"/>`)
  return withoutSlides.replace('</Relationships>', `${relationships.join('')}</Relationships>`)
}

function withSlideContentTypes(xml: string, count: number) {
  const withoutSlides = xml.replace(/<Override\b(?=[^>]*\/ppt\/slides\/slide\d+\.xml")[^>]*\/>/g, '')
  const overrides = Array.from({ length: count }, (_item, index) => `<Override PartName="/ppt/slides/slide${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/>`)
  return withoutSlides.replace('</Types>', `${overrides.join('')}</Types>`)
}

function withoutNotesRelationship(xml: string) {
  return xml.replace(/<Relationship\b(?=[^>]*\/relationships\/notesSlide")[^>]*\/>/g, '')
}

type CaptureBlobMap = ReadonlyMap<string, Blob>

type CaptureSize = {
  width: number
  height: number
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

function projectForCanvasProjection(source: PresentationSourceSnapshot): ProjectRow {
  const snapshot = source.project.snapshot
  return {
    id: source.project.id,
    slug: '',
    code: '',
    name: '',
    scenario: '',
    city: '',
    current_phase_id: snapshot.project.current_phase_id,
    current_step_id: snapshot.project.current_step_id,
    status_label: snapshot.project.status_summary,
    as_of_date: snapshot.as_of_date,
    version: source.project.version,
    snapshot,
    updated_at: source.created_at,
  }
}

function validateCaptureBinding(source: PresentationSourceSnapshot, slide: PresentationSlide) {
  if (!slide.capture_id) return undefined
  const binding = source.captures?.find((capture) => capture.id === slide.capture_id)
  const expectedEntityId = source.config.scope === 'general'
    ? `reference:${source.project.snapshot.reference.id}`
    : source.project.id
  const expectedAsOfDate = source.config.scope === 'general'
    ? source.project.snapshot.reference.effective_date
    : source.project.snapshot.as_of_date
  const project = projectForCanvasProjection(source)
  const projection = buildCanvasProjection(project, source.config.scope)
  const referenceProjection = buildCanvasProjection(project, 'general')
  const expectedSignature = binding ? canvasSceneProjectionSignature({
    projection,
    referenceProjection,
    mode: binding.mode,
    visibleLayers: binding.visible_layers,
  }) : undefined
  const hasSceneState = binding
    && (binding.view_state === 'overview' || binding.view_state === 'focus')
    && (binding.view_state !== 'focus' || Boolean(binding.consulted_phase_id))
    && (binding.mode !== 'layers-3d' || binding.active_layer === 'reference' || binding.active_layer === 'project')
    && Boolean(binding.projection_signature)
    && binding.renderer_version === CANVAS_RENDERER_VERSION
    && /^[0-9a-f]{64}$/.test(binding.png_sha256)
  if (!binding
    || binding.slide_id !== slide.id
    || binding.scope !== source.config.scope
    || binding.entity_id !== expectedEntityId
    || binding.source_version !== source.project.version
    || binding.reference_id !== source.project.snapshot.reference.id
    || binding.as_of_date !== expectedAsOfDate
    || !hasSceneState
    || binding.projection_signature !== expectedSignature) {
    throw new Error(`La capture « ${slide.capture_id} » ne correspond pas à l’état de cette présentation.`)
  }
  return binding
}

async function capturePngBytes(source: PresentationSourceSnapshot, slide: PresentationSlide, captureBlobs: CaptureBlobMap) {
  if (!slide.capture_id) return undefined
  const binding = validateCaptureBinding(source, slide)
  const blob = captureBlobs.get(slide.capture_id)
  if (!blob) throw new Error(`La capture « ${slide.capture_id} » liée à la diapositive « ${slide.title} » est introuvable.`)
  const bytes = new Uint8Array(await blob.arrayBuffer())
  if (bytes.length < PNG_SIGNATURE.length || !PNG_SIGNATURE.every((value, index) => bytes[index] === value)) {
    throw new Error(`La capture « ${slide.capture_id} » n’est pas une image PNG valide.`)
  }
  if (!binding || await sha256Blob(blob) !== binding.png_sha256) {
    throw new Error(`La capture « ${slide.capture_id} » ne correspond plus à son empreinte enregistrée.`)
  }
  return bytes
}

function captureSize(source: PresentationSourceSnapshot, captureId: string, fallback: CaptureSize): CaptureSize {
  const binding = source.captures?.find((capture) => capture.id === captureId)
  if (!binding || binding.width <= 0 || binding.height <= 0) return fallback
  return { width: binding.width, height: binding.height }
}

function containRect(source: CaptureSize, frame: { x: number; y: number; width: number; height: number }) {
  const scale = Math.min(frame.width / source.width, frame.height / source.height)
  const width = Math.round(source.width * scale)
  const height = Math.round(source.height * scale)
  return {
    x: Math.round(frame.x + (frame.width - width) / 2),
    y: Math.round(frame.y + (frame.height - height) / 2),
    width,
    height,
  }
}

function withPngContentType(xml: string) {
  if (/<Default\b(?=[^>]*Extension="png")(?=[^>]*ContentType="image\/png")[^>]*\/>/i.test(xml)) return xml
  return xml.replace('</Types>', '<Default Extension="png" ContentType="image/png" /></Types>')
}

function withCaptureRelationship(xml: string, relationshipId: string, filename: string) {
  const relationship = `<Relationship Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/${filename}" Id="${relationshipId}" />`
  return xml.replace('</Relationships>', `${relationship}</Relationships>`)
}

function withCapturePicture(
  xml: string,
  input: { captureId: string; relationshipId: string; shapeId: number; size: CaptureSize },
) {
  const frame = {
    x: 685_800,
    y: 2_180_000,
    width: 10_820_400,
    height: 4_000_000,
  }
  const box = containRect(input.size, frame)
  const captureId = xmlEscape(input.captureId)
  const relationshipId = xmlEscape(input.relationshipId)
  const overlay = [
    '<p:sp>',
    '<p:nvSpPr>',
    `<p:cNvPr id="${input.shapeId - 1}" name="Zone de capture 3D" />`,
    '<p:cNvSpPr />',
    '<p:nvPr />',
    '</p:nvSpPr>',
    '<p:spPr>',
    '<a:xfrm xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">',
    `<a:off x="${frame.x}" y="${frame.y}" />`,
    `<a:ext cx="${frame.width}" cy="${frame.height}" />`,
    '</a:xfrm>',
    '<a:prstGeom prst="rect" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:avLst /></a:prstGeom>',
    '<a:solidFill xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:srgbClr val="FBFAF8" /></a:solidFill>',
    '<a:ln xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:noFill /></a:ln>',
    '</p:spPr>',
    '</p:sp>',
    '<p:pic>',
    '<p:nvPicPr>',
    `<p:cNvPr id="${input.shapeId}" name="Capture 3D Suivi Aziz" descr="Capture ${captureId}" />`,
    '<p:cNvPicPr><a:picLocks noChangeAspect="1" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" /></p:cNvPicPr>',
    '<p:nvPr />',
    '</p:nvPicPr>',
    '<p:blipFill>',
    `<a:blip r:embed="${relationshipId}" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" />`,
    '<a:stretch xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:fillRect /></a:stretch>',
    '</p:blipFill>',
    '<p:spPr>',
    '<a:xfrm xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">',
    `<a:off x="${box.x}" y="${box.y}" />`,
    `<a:ext cx="${box.width}" cy="${box.height}" />`,
    '</a:xfrm>',
    '<a:prstGeom prst="rect" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:avLst /></a:prstGeom>',
    '<a:ln xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:noFill /></a:ln>',
    '</p:spPr>',
    '</p:pic>',
  ].join('')
  return xml.replace('</p:spTree>', `${overlay}</p:spTree>`)
}

export async function buildPresentationPowerPoint(
  source: PresentationSourceSnapshot,
  templateUrl = '/templates/suiviaziz-presentation-v1.pptx',
  captureBlobs: CaptureBlobMap = new Map(),
) {
  const [{ default: JSZip }, response] = await Promise.all([import('jszip'), fetch(templateUrl)])
  if (!response.ok) throw new Error('Le modèle PowerPoint Sablux n’a pas pu être chargé.')
  const zip = await JSZip.loadAsync(await response.arrayBuffer())
  const presentationPath = 'ppt/presentation.xml'
  const relationshipsPath = 'ppt/_rels/presentation.xml.rels'
  const contentTypesPath = '[Content_Types].xml'
  const presentationXml = await zip.file(presentationPath)?.async('string')
  const relationshipsXml = await zip.file(relationshipsPath)?.async('string')
  const contentTypesXml = await zip.file(contentTypesPath)?.async('string')
  if (!presentationXml || !relationshipsXml || !contentTypesXml) throw new Error('Le modèle PowerPoint Sablux est incomplet.')

  const selectedParts = await Promise.all(source.slides.map(async (slide) => {
    const templateNumber = TEMPLATE_SLIDE[slide.key]
    const slidePath = `ppt/slides/slide${templateNumber}.xml`
    const relsPath = `ppt/slides/_rels/slide${templateNumber}.xml.rels`
    const xml = await zip.file(slidePath)?.async('string')
    const rels = await zip.file(relsPath)?.async('string')
    if (!xml || !rels) throw new Error(`Le modèle PowerPoint « ${slide.key} » est introuvable.`)
    return { slide, xml, rels, capturePng: await capturePngBytes(source, slide, captureBlobs) }
  }))

  zip.file(presentationPath, withSlideEntries(presentationXml, selectedParts.length))
  zip.file(relationshipsPath, withSlideRelationships(relationshipsXml, selectedParts.length))
  const selectedContentTypes = withSlideContentTypes(contentTypesXml, selectedParts.length)
  zip.file(contentTypesPath, selectedParts.some((part) => part.capturePng) ? withPngContentType(selectedContentTypes) : selectedContentTypes)
  for (const [index, part] of selectedParts.entries()) {
    const outputNumber = index + 1
    let slideXml = replaceSequenceLabels(replaceTokens(part.xml, {
      ...part.slide.tokens,
      PAGE_NUMBER: String(outputNumber).padStart(2, '0'),
    }), part.slide)
    let slideRelationships = withoutNotesRelationship(part.rels)
    if (part.capturePng && part.slide.capture_id) {
      const filename = `suiviaziz-capture-${outputNumber}.png`
      const relationshipId = `rIdSuiviAzizCapture${outputNumber}`
      zip.file(`ppt/media/${filename}`, part.capturePng)
      slideXml = withCapturePicture(slideXml, {
        captureId: part.slide.capture_id,
        relationshipId,
        shapeId: 9_000 + outputNumber,
        size: captureSize(source, part.slide.capture_id, { width: 1_920, height: 1_080 }),
      })
      slideRelationships = withCaptureRelationship(slideRelationships, relationshipId, filename)
    }
    zip.file(`ppt/slides/slide${outputNumber}.xml`, slideXml)
    zip.file(`ppt/slides/_rels/slide${outputNumber}.xml.rels`, slideRelationships)
  }

  const corePath = 'docProps/core.xml'
  const core = await zip.file(corePath)?.async('string')
  if (core) {
    const title = xmlEscape(source.slides[0]?.title ?? 'Présentation Suivi Aziz')
    const subject = xmlEscape(`Édition ${source.edition_id} · projet v${source.project.version}`)
    let updated = core
      .replace(/<dc:title>[\s\S]*?<\/dc:title>/, `<dc:title>${title}</dc:title>`)
      .replace(/<dc:subject>[\s\S]*?<\/dc:subject>/, `<dc:subject>${subject}</dc:subject>`)
    if (!updated.includes('<dc:subject>')) {
      updated = updated.replace(/<\/(cp:)?coreProperties>/, (_match, prefix: string | undefined) => `<dc:subject>${subject}</dc:subject></${prefix ?? ''}coreProperties>`)
    }
    zip.file(corePath, updated)
  }

  return zip.generateAsync({ type: 'blob', mimeType: PPTX_MIME, compression: 'DEFLATE' })
}

type PdfRuntime = Awaited<ReturnType<typeof importPdfRuntime>>

async function importPdfRuntime() {
  return import('pdf-lib')
}

function color(hex: string, runtime: PdfRuntime) {
  const normalized = hex.replace('#', '')
  return runtime.rgb(
    Number.parseInt(normalized.slice(0, 2), 16) / 255,
    Number.parseInt(normalized.slice(2, 4), 16) / 255,
    Number.parseInt(normalized.slice(4, 6), 16) / 255,
  )
}

function wrapText(text: string, maximumWidth: number, font: { widthOfTextAtSize: (value: string, size: number) => number }, size: number) {
  const lines: string[] = []
  for (const paragraph of text.split('\n')) {
    if (!paragraph) {
      lines.push('')
      continue
    }
    let current = ''
    for (const word of paragraph.split(/\s+/)) {
      const candidate = current ? `${current} ${word}` : word
      if (font.widthOfTextAtSize(candidate, size) <= maximumWidth || !current) current = candidate
      else {
        lines.push(current)
        current = word
      }
    }
    if (current) lines.push(current)
  }
  return lines
}

function drawWrapped(
  page: { drawText: Function },
  value: string,
  options: { x: number; y: number; width: number; size: number; font: any; fill: any; lineHeight?: number; maxLines?: number },
) {
  const lines = wrapText(value, options.width, options.font, options.size).slice(0, options.maxLines ?? 12)
  const lineHeight = options.lineHeight ?? options.size * 1.22
  lines.forEach((line, index) => page.drawText(line, {
    x: options.x,
    y: options.y - index * lineHeight,
    size: options.size,
    font: options.font,
    color: options.fill,
  }))
}

export async function buildPresentationPdf(
  source: PresentationSourceSnapshot,
  logoUrl = '/assets/brand/sablux-ci-logo.png',
  captureBlobs: CaptureBlobMap = new Map(),
) {
  const runtime = await importPdfRuntime()
  const document = await runtime.PDFDocument.create()
  const regular = await document.embedFont(runtime.StandardFonts.Helvetica)
  const bold = await document.embedFont(runtime.StandardFonts.HelveticaBold)
  const logoResponse = await fetch(logoUrl)
  if (!logoResponse.ok) throw new Error('Le logo Sablux n’a pas pu être chargé pour le PDF.')
  const logo = await document.embedPng(await logoResponse.arrayBuffer())
  const W = 960
  const H = 540
  const purple = color('#8F4396', runtime)
  const purpleDark = color('#5D2864', runtime)
  const purpleSoft = color('#F2E7F4', runtime)
  const ink = color('#27232B', runtime)
  const muted = color('#746E77', runtime)
  const line = color('#DDD9DE', runtime)
  const paper = color('#FBFAF8', runtime)
  const white = runtime.rgb(1, 1, 1)
  const green = color('#3C8C6B', runtime)
  const orange = color('#E98214', runtime)
  const captureImages = new Map<string, any>()
  for (const slide of source.slides) {
    if (!slide.capture_id || captureImages.has(slide.capture_id)) continue
    const bytes = await capturePngBytes(source, slide, captureBlobs)
    if (bytes) captureImages.set(slide.capture_id, await document.embedPng(bytes))
  }

  function addChrome(page: any, index: number) {
    page.drawImage(logo, { x: 48, y: H - 67, width: 132, height: 57 })
    page.drawText(`SUIVI AZIZ  ·  ${String(index + 1).padStart(2, '0')}`, { x: W - 190, y: H - 38, size: 9, font: bold, color: muted })
    page.drawLine({ start: { x: 48, y: 34 }, end: { x: W - 48, y: 34 }, thickness: 1, color: line })
    page.drawText(`Sablux Côte d’Ivoire · ${source.edition_id}`, { x: 48, y: 19, size: 8, font: regular, color: muted })
    page.drawText(String(index + 1), { x: W - 57, y: 19, size: 8, font: bold, color: purple })
  }

  function addTitle(page: any, slide: PresentationSlide) {
    drawWrapped(page, slide.title, { x: 48, y: H - 108, width: W - 96, size: 27, font: bold, fill: ink, lineHeight: 31, maxLines: 2 })
    if (slide.subtitle) drawWrapped(page, slide.subtitle, { x: 48, y: H - 174, width: W - 96, size: 12, font: regular, fill: muted, lineHeight: 15, maxLines: 2 })
  }

  source.slides.forEach((slide, index) => {
    const page = document.addPage([W, H])
    page.drawRectangle({ x: 0, y: 0, width: W, height: H, color: slide.key === 'edition' ? purpleDark : paper })

    if (slide.key === 'cover') {
      page.drawRectangle({ x: 0, y: 0, width: 338, height: H, color: purple })
      page.drawImage(logo, { x: 430, y: H - 92, width: 160, height: 69 })
      drawWrapped(page, slide.title, { x: 48, y: H - 155, width: 244, size: 34, font: bold, fill: white, lineHeight: 38, maxLines: 3 })
      drawWrapped(page, slide.subtitle ?? '', { x: 48, y: H - 292, width: 244, size: 18, font: regular, fill: white, lineHeight: 22, maxLines: 3 })
      page.drawRectangle({ x: 48, y: 128, width: 82, height: 3, color: white })
      page.drawText(slide.tokens.AS_OF ?? '', { x: 48, y: 102, size: 11, font: regular, color: white })
      page.drawText(slide.tokens.SCOPE_LABEL ?? '', { x: 48, y: 48, size: 10, font: bold, color: white })
      drawWrapped(page, slide.tokens.COVER_HEADING ?? 'Objet de la présentation', { x: 430, y: H - 164, width: 430, size: 29, font: bold, fill: ink, lineHeight: 34, maxLines: 3 })
      drawWrapped(page, slide.statement ?? '', { x: 430, y: H - 286, width: 430, size: 16, font: regular, fill: muted, lineHeight: 20, maxLines: 5 })
      page.drawText(slide.detail ?? '', { x: 430, y: 118, size: 12, font: bold, color: purple })
      page.drawText(source.edition_id, { x: 430, y: 82, size: 9, font: regular, color: muted })
      return
    }

    if (slide.key === 'edition') {
      page.drawText(slide.tokens.EDITION_KICKER ?? 'RÉFÉRENCE DE LA PRÉSENTATION', { x: 48, y: H - 58, size: 11, font: bold, color: color('#E3CFE6', runtime) })
      drawWrapped(page, slide.title, { x: 48, y: H - 118, width: 760, size: 30, font: bold, fill: white, lineHeight: 35, maxLines: 2 })
      page.drawText('IDENTIFIANT', { x: 48, y: 276, size: 10, font: bold, color: color('#D6B7DA', runtime) })
      page.drawText(source.edition_id, { x: 48, y: 242, size: 18, font: bold, color: white })
      page.drawText('SITUATION', { x: 48, y: 190, size: 10, font: bold, color: color('#D6B7DA', runtime) })
      page.drawText(slide.tokens.AS_OF ?? '', { x: 48, y: 156, size: 18, font: bold, color: white })
      page.drawLine({ start: { x: 470, y: 116 }, end: { x: 470, y: 308 }, thickness: 1, color: color('#9C60A3', runtime) })
      page.drawText('SOURCE', { x: 516, y: 276, size: 10, font: bold, color: color('#D6B7DA', runtime) })
      drawWrapped(page, slide.detail ?? '', { x: 516, y: 242, width: 370, size: 18, font: bold, fill: white, lineHeight: 23, maxLines: 2 })
      page.drawText('CONTENU', { x: 516, y: 174, size: 10, font: bold, color: color('#D6B7DA', runtime) })
      drawWrapped(page, slide.tokens.CONTENT_SUMMARY ?? '', { x: 516, y: 144, width: 370, size: 12, font: regular, fill: white, lineHeight: 16, maxLines: 4 })
      return
    }

    addChrome(page, index)
    addTitle(page, slide)

    const capture = slide.capture_id ? captureImages.get(slide.capture_id) : undefined
    if (capture && slide.capture_id) {
      const bindingSize = captureSize(source, slide.capture_id, { width: capture.width, height: capture.height })
      const box = containRect(bindingSize, { x: 48, y: 48, width: W - 96, height: 292 })
      page.drawImage(capture, { x: box.x, y: box.y, width: box.width, height: box.height })
      return
    }

    if (slide.key === 'process' && slide.phases) {
      const phases = slide.phases ?? []
      page.drawLine({ start: { x: 96, y: 248 }, end: { x: 864, y: 248 }, thickness: 5, color: line })
      phases.forEach((phase, phaseIndex) => {
        const x = 108 + phaseIndex * (744 / Math.max(1, phases.length - 1))
        page.drawCircle({ x, y: 248, size: 25, color: phaseIndex === 0 ? purple : paper, borderColor: phaseIndex === 0 ? purple : muted, borderWidth: 2 })
        page.drawText(String(phaseIndex + 1).padStart(2, '0'), { x: x - 8, y: 243, size: 10, font: bold, color: phaseIndex === 0 ? white : muted })
        drawWrapped(page, phase.name, { x: x - 72, y: 190, width: 144, size: 12, font: bold, fill: phaseIndex === 0 ? purple : ink, lineHeight: 15, maxLines: 3 })
        page.drawText(phase.detail, { x: x - 44, y: 132, size: 9, font: regular, color: muted })
      })
    } else if (slide.key === 'current') {
      page.drawRectangle({ x: 48, y: 112, width: 6, height: 232, color: purple })
      page.drawText('ÉTAPE ACTUELLE', { x: 78, y: 322, size: 10, font: bold, color: purple })
      drawWrapped(page, slide.statement ?? '', { x: 78, y: 286, width: 430, size: 22, font: bold, fill: ink, lineHeight: 26, maxLines: 3 })
      page.drawText(slide.tokens.CURRENT_STATUS ?? '', { x: 78, y: 188, size: 18, font: bold, color: orange })
      drawWrapped(page, slide.tokens.CURRENT_DETAIL ?? '', { x: 78, y: 156, width: 420, size: 11, font: regular, fill: muted, maxLines: 2 })
      page.drawText(slide.metric ?? '', { x: 630, y: 246, size: 62, font: bold, color: purple })
      drawWrapped(page, slide.metricLabel ?? '', { x: 580, y: 190, width: 250, size: 15, font: bold, fill: ink, maxLines: 2 })
      drawWrapped(page, slide.detail ?? '', { x: 564, y: 138, width: 290, size: 11, font: regular, fill: muted, maxLines: 3 })
    } else if (slide.columns) {
      slide.columns.slice(0, 2).forEach((column, columnIndex) => {
        const x = 48 + columnIndex * 456
        if (columnIndex) page.drawLine({ start: { x: x - 24, y: 96 }, end: { x: x - 24, y: 332 }, thickness: 1, color: line })
        page.drawText(column.label.toUpperCase(), { x, y: 320, size: 10, font: bold, color: column.tone === 'green' ? green : column.tone === 'purple' ? purple : muted })
        drawWrapped(page, bullets(column.items), { x, y: 276, width: 390, size: 15, font: regular, fill: ink, lineHeight: 23, maxLines: 10 })
      })
    } else if (slide.key === 'dates') {
      const timeline = slide.timeline ?? []
      page.drawLine({ start: { x: 94, y: 240 }, end: { x: 860, y: 240 }, thickness: 5, color: line })
      timeline.forEach((item, itemIndex) => {
        const x = 108 + itemIndex * (744 / Math.max(1, timeline.length - 1))
        page.drawCircle({ x, y: 240, size: 23, color: itemIndex === 0 ? purple : paper, borderColor: itemIndex === 0 ? purple : muted, borderWidth: 2 })
        page.drawText(item.date, { x: x - 48, y: 285, size: 10, font: bold, color: itemIndex === 0 ? purple : muted })
        drawWrapped(page, item.label, { x: x - 68, y: 190, width: 136, size: 12, font: bold, fill: ink, maxLines: 3 })
      })
      drawWrapped(page, slide.detail ?? '', { x: 160, y: 112, width: 640, size: 13, font: regular, fill: muted, maxLines: 3 })
    } else if (slide.key === 'derogation') {
      drawWrapped(page, slide.statement ?? '', { x: 48, y: 304, width: 350, size: 23, font: bold, fill: orange, maxLines: 2 })
      drawWrapped(page, slide.detail ?? '', { x: 48, y: 232, width: 350, size: 14, font: regular, fill: muted, maxLines: 5 })
      const items = slide.items ?? []
      page.drawLine({ start: { x: 466, y: 236 }, end: { x: 838, y: 236 }, thickness: 4, color: line })
      items.forEach((item, itemIndex) => {
        const x = 478 + itemIndex * 174
        page.drawCircle({ x, y: 236, size: 23, color: paper, borderColor: orange, borderWidth: 2 })
        page.drawText(`D${(slide.sequenceStart ?? 0) + itemIndex + 1}`, { x: x - 8, y: 231, size: 10, font: bold, color: orange })
        drawWrapped(page, item.title, { x: x - 70, y: 178, width: 140, size: 12, font: bold, fill: ink, maxLines: 4 })
      })
    } else if (slide.items) {
      slide.items.slice(0, 5).forEach((item, itemIndex) => {
        const y = 326 - itemIndex * 55
        page.drawText(String((slide.sequenceStart ?? 0) + itemIndex + 1).padStart(2, '0'), { x: 48, y, size: 14, font: bold, color: purple })
        drawWrapped(page, item.title, { x: 106, y: y + 2, width: 500, size: 15, font: bold, fill: ink, maxLines: 2 })
        if (item.detail || item.meta) drawWrapped(page, [item.detail, item.meta].filter(Boolean).join(' · '), { x: 644, y: y + 2, width: 268, size: 10, font: regular, fill: muted, maxLines: 2 })
        if (itemIndex < 4) page.drawLine({ start: { x: 48, y: y - 18 }, end: { x: W - 48, y: y - 18 }, thickness: 1, color: line })
      })
    }
  })

  document.setTitle(source.slides[0]?.title ?? 'Présentation Suivi Aziz')
  document.setSubject(`Édition ${source.edition_id} · projet v${source.project.version}`)
  document.setKeywords(['Suivi Aziz', source.edition_id, source.project.snapshot.reference.id])
  document.setProducer('Suivi Aziz · Sablux Côte d’Ivoire')
  const bytes = await document.save()
  return new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' })
}

export async function sha256Blob(blob: Blob) {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return Array.from(new Uint8Array(digest)).map((value) => value.toString(16).padStart(2, '0')).join('')
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}
